import { BASE_LOGIN_CANVAS_HEIGHT, BASE_LOGIN_FRAME_WIDTH } from './layout';

type ScaleUtility = {
  px: (figmaPx: number) => string;
  value: (figmaPx: number) => number;
};

type ThemeColors = {
  brand: {
    primary: string;
    secondary: string;
    emphasis: string;
  };
  support: {
    danger: string;
    dangerSoft: string;
    infoSubtle: string;
    rating: string;
    success: string;
  };
  surface: {
    base: string;
    subtle: string;
    preview: string;
    tertiary: string;
    progressTrack: string;
    backdrop: string;
    overlay: string;
  };
  border: {
    subtle: string;
    divider: string;
    codeEntry: string;
    emphasis: string;
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
    subdued: string;
  };
};

export type AppTheme = {
  frame: {
    width: number;
    height: number;
  };
  scale: ScaleUtility;
  colors: ThemeColors;
};

const themeColors: ThemeColors = {
  brand: {
    primary: '#ffbe00',
    secondary: '#ffd266',
    emphasis: '#f5bc3f',
  },
  support: {
    danger: '#f22626',
    dangerSoft: '#e64646',
    infoSubtle: '#b5d2f0',
    rating: 'rgba(255, 190, 0, 0.45)',
    success: '#26a143',
  },
  surface: {
    base: '#ffffff',
    subtle: '#ececec',
    preview: '#f1f2f2',
    tertiary: '#d9d9d9',
    progressTrack: '#c4c4c4',
    backdrop: 'rgba(70, 70, 70, 0.7)',
    overlay: 'rgba(27, 27, 27, 0.1)',
  },
  border: {
    subtle: 'rgba(0, 0, 0, 0.04)',
    divider: 'rgba(0, 0, 0, 0.2)',
    codeEntry: '#000000',
    emphasis: 'rgba(0, 0, 0, 0.5)',
  },
  text: {
    primary: '#1b1b1b',
    secondary: '#353535',
    muted: '#c4c4c4',
    subdued: 'rgba(0, 0, 0, 0.7)',
  },
};

function normalizeViewportAxis(
  viewportAxis: number,
  fallbackValue: number,
): number {
  if (!Number.isFinite(viewportAxis)) {
    return fallbackValue;
  }

  return Math.max(1, Math.floor(viewportAxis));
}

function clampScaleRatio(scaleRatio: number): number {
  if (!Number.isFinite(scaleRatio)) {
    return 1;
  }

  return scaleRatio;
}

function createScaleUtility(scaleRatio: number): ScaleUtility {
  const toScaledValue = (figmaPx: number) => Math.round(figmaPx * scaleRatio);

  return {
    px: (figmaPx: number) => `${toScaledValue(figmaPx)}px`,
    value: toScaledValue,
  };
}

export function createAppTheme(
  viewportWidth: number,
  viewportHeight = BASE_LOGIN_CANVAS_HEIGHT,
): AppTheme {
  const normalizedViewportWidth = normalizeViewportAxis(
    viewportWidth,
    BASE_LOGIN_FRAME_WIDTH,
  );
  const normalizedViewportHeight = normalizeViewportAxis(
    viewportHeight,
    BASE_LOGIN_CANVAS_HEIGHT,
  );
  const scaleRatio = clampScaleRatio(
    Math.min(
      normalizedViewportWidth / BASE_LOGIN_FRAME_WIDTH,
      normalizedViewportHeight / BASE_LOGIN_CANVAS_HEIGHT,
    ),
  );
  const scale = createScaleUtility(scaleRatio);

  return {
    frame: {
      width: scale.value(BASE_LOGIN_FRAME_WIDTH),
      height: scale.value(BASE_LOGIN_CANVAS_HEIGHT),
    },
    colors: themeColors,
    scale,
  };
}
